import React from 'react'

const NewConversationCountContext = React.createContext(null)

// Checks if an element has scrolled out of it's parent
const isOffScreen = ref => {
  if (!ref || !ref.current) {
    return [false, false, false]
  }

  const bound = ref.current.getBoundingClientRect()
  const parentBound = ref.current.parentNode.getBoundingClientRect()

  const offset = 50
  const offTop = bound.top + offset <= parentBound.top
  const offBot = bound.bottom - offset >= parentBound.bottom
  const isOff = offTop || offBot

  return [isOff, isOff && offTop, isOff && offBot]
}

const NewConversationCountProvider = ({ children }) => {
  const state = React.useRef({})
  const [changed, setChanged] = React.useState(false)
  const [count, setCount] = React.useState(0)

  const triggerRecheck = React.useCallback(() => {
    setChanged(true)
  }, [])

  // Updates the position of this component
  const updatePosition = React.useCallback(({ id, isNew, ref }) => {
    const [isOff, above, below] = isOffScreen(ref)
    const current = state.current[id]

    if (current) {
      const old = current.updated
      let newVal = isNew && isOff

      if (!isOff) {
        newVal = false
      }

      state.current[id] = { updated: newVal, isNew, above, below, ref }

      if (old !== newVal) {
        setChanged(true)
      }
    } else {
      state.current[id] = {
        updated: isNew && isOff,
        isNew,
        above,
        below,
        ref
      }

      setChanged(true)
    }
  }, [])

  // Gets an updated ref from the state
  const getNextID = React.useCallback(() => {
    if (count === 0) {
      return null
    }

    let nextID = null

    Object.entries(state.current).forEach(([id, { updated }]) => {
      if (!nextID && updated) {
        nextID = id
      }
    })

    return {
      id: nextID,
      above: state.current[nextID]?.above,
      below: state.current[nextID]?.below,
      onClick: () => {
        state.current[nextID] = {
          ...state.current[nextID],
          updated: false,
          isNew: false
        }
        setChanged(true)
      }
    }
  }, [count])

  // Counts number of updated elements
  React.useEffect(() => {
    if (changed) {
      let count = 0

      Object.entries(state.current).forEach(([id, { isNew, ref }]) => {
        const [isOff, above, below] = isOffScreen(ref)

        if (isOff && isNew) {
          count++
        }

        state.current[id] = {
          ...state.current[id],
          updated: isNew && isOff,
          above,
          below
        }
      })

      setCount(count)
      setChanged(false)
    }
  }, [changed])

  return (
    <NewConversationCountContext.Provider
      value={{
        count,
        getNextID,
        updatePosition,
        triggerRecheck,
        state: state.current
      }}
    >
      {children}
    </NewConversationCountContext.Provider>
  )
}

export default NewConversationCountProvider

export function useNewConversationCount () {
  return React.useContext(NewConversationCountContext)
}
